import { useEffect, useRef, useState } from "react";
import { Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
} from "chart.js";
import { ArrowLeft } from "@mui/icons-material";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  PointElement
);

export default function ChartPanel(props) {
  const { styles, data, showNextQuestionBtn, chartPanel, setChartPanel } = props;

  const animationFrameId = useRef(null);
  const chartRef = useRef(null);
  const chartWrapperRef = useRef(null);

  const [chartDimensions, setChartDimensions] = useState({
    width: window.innerWidth <= 600 ? 90 : window.innerWidth <= 800 ? 50 : 25,
  });

  const toggleChartPanel = () => {
    setChartPanel(!chartPanel);
  };

  const resizePanelMouse = (e) => {
    handleResize(e.clientX, e.clientY);
  };

  const resizePanelTouch = (e) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleResize(touch.clientX, touch.clientY);
    }
  };

  const handleResize = (clientX, clientY) => {
    if (!chartWrapperRef.current) return;

    if (animationFrameId.current) return; // skip if frame already scheduled

    animationFrameId.current = requestAnimationFrame(() => {
      animationFrameId.current = null;

      const newWidth = Math.max(
        window.innerWidth <= 800 ? 50 : 25,
        Math.min(
          ((window.innerWidth - clientX) / window.innerWidth) * 100,
          window.innerWidth <= 800 ? 90 : 43
        )
      );
      const newHeight = Math.max(
        20,
        Math.min(
          ((window.innerHeight - clientY) / window.innerHeight) * 100,
          97
        )
      );

      setChartDimensions({ width: newWidth, height: newHeight });
    });
  };

  const startResizing = (e) => {
    e.preventDefault();
    document.addEventListener("mousemove", resizePanelMouse);
    document.addEventListener("mouseup", stopResizing);

    document.addEventListener("touchmove", resizePanelTouch, {
      passive: false,
    });
    document.addEventListener("touchend", stopResizing);
  };

  const stopResizing = () => {
    document.removeEventListener("mousemove", resizePanelMouse);
    document.removeEventListener("mouseup", stopResizing);

    document.removeEventListener("touchmove", resizePanelTouch);
    document.removeEventListener("touchend", stopResizing);
  };

  useEffect(() => {
    const chart = chartRef.current;
    const canvas = chart?.canvas;

    const handleClick = (event) => {
      const points = chart.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: false },
        false
      );
      if (points.length > 0) {
        chart.tooltip.setActiveElements(points, { x: event.x, y: event.y });
        chart.update();
      }
    };

    canvas?.addEventListener("click", handleClick);
    return () => canvas?.removeEventListener("click", handleClick);
  }, []);

  return (
    <div
      ref={chartWrapperRef}
      className={`${styles.chart_panel} ${chartPanel && styles.open}`}
      style={{
        height: `${chartDimensions.width}vw`,
        width: `${chartPanel ? `${chartDimensions.width}vw` : "0%"}`,
      }}
    >
      <div
        className={`${styles.collapse_btn} ${!chartPanel && styles.close} ${
          showNextQuestionBtn && styles.animate
        }`}
        onClick={toggleChartPanel}
      >
        <ArrowLeft className={styles.icon} />
      </div>
      <Radar
        className={styles.chart}
        data={data}
        ref={chartRef}
        options={{
          events: ["click"],
          showTooltip: false,
          animation: {
            duration: 0,
            onComplete: function (context) {
              var ctx = context.chart.ctx;
              ctx.font = `bold ${window.innerWidth <= 800 ? 10 : 16}px poppins`;
              ctx.textAlign = "center";
              ctx.textBaseline = "bottom";

              // const datasets = context.chart.getDatasetMeta(0);
              if (window.innerWidth <= 600) return;
              context.chart._metasets.forEach(function (dataset) {
                for (var i = 0; i < dataset.data.length; i++) {
                  var model = dataset.data[i];
                  ctx.fillText(
                    dataset.data[i]?.$context.raw,
                    model.x,
                    model.y - 5
                  );
                }
              });
            },
          },
          responsive: true,
          scales: {
            r: {
              beginAtZero: true,
              pointLabels: {
                font: {
                  size: window.innerWidth <= 800 ? 10 : 16,
                  family: "poppins",
                },
              },
              angleLines: {
                display: true,
              },
              ticks: {
                stepSize: 50,
                color: "#333333",
                font: {
                  size: 10,
                  family: "poppins",
                },
                backdropPadding: 2,
                backdropBorderRadius: 2,
                backdropWidth: 40,
                backdropHeight: 20,
                showLabelBackdrop: true,

                callback: (value) => {
                  const levelIndex = [100, 200, 400, 800, 1600].indexOf(value);
                  return levelIndex !== -1
                    ? `Level ${levelIndex + 1}`
                    : `${value % 100 === 0 ? value + " XP" : ""}`;
                },
                backdropColor: "#f6f6f6",
              },
            },
          },
          plugins: {
            legend: {
              position: "top",
            },
          },
        }}
      />
      <div
        className={styles.resize_btn}
        onMouseDown={startResizing}
        onTouchStart={startResizing}
      >
        <ArrowLeft className={styles.icon} />
      </div>
    </div>
  );
}
